import { useState, useMemo } from 'react';

import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import {
  logsAuditoriaConfiguracoes,
  filtrarLogsAuditoriaConfig,
  type LogAuditoriaConfiguracao,
} from '@/mocks/backoffice/auditoriaConfiguracoes';
import { empresasPlataforma } from '@/mocks/backoffice/empresas';
import { campanhasBackoffice } from '@/mocks/backoffice/campanhasBackoffice';
import { getAcaoConfig, getModuloLabel, getModuloColor } from '@/mocks/backoffice/auditoria';

export default function AuditoriaConfiguracoesPage() {
  const [empresaFiltro, setEmpresaFiltro] = useState('');
  const [campanhaFiltro, setCampanhaFiltro] = useState('');
  const [escopoFiltro, setEscopoFiltro] = useState<'todos' | 'empresa' | 'campanha' | 'participante'>('todos');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [detalheLog, setDetalheLog] = useState<LogAuditoriaConfiguracao | null>(null);

  const itensPorPagina = 10;

  const logsFiltrados = useMemo(() => {
    return filtrarLogsAuditoriaConfig(
      empresaFiltro || undefined,
      campanhaFiltro || undefined,
      undefined,
      escopoFiltro === 'todos' ? undefined : escopoFiltro
    ).filter((log) => {
      if (!busca) return true;
      const b = busca.toLowerCase();
      return (
        log.detalhes.toLowerCase().includes(b) ||
        log.usuario.toLowerCase().includes(b) ||
        log.empresaNome?.toLowerCase().includes(b) ||
        log.campanhaNome?.toLowerCase().includes(b) ||
        log.participanteNome?.toLowerCase().includes(b) ||
        log.chaveConfig?.toLowerCase().includes(b)
      );
    });
  }, [empresaFiltro, campanhaFiltro, escopoFiltro, busca]);

  const totalPaginas = Math.max(1, Math.ceil(logsFiltrados.length / itensPorPagina));
  const logsPaginados = logsFiltrados.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina);

  const stats = useMemo(() => {
    return {
      total: logsFiltrados.length,
      empresa: logsFiltrados.filter((l) => l.escopo === 'empresa').length,
      campanha: logsFiltrados.filter((l) => l.escopo === 'campanha').length,
      participante: logsFiltrados.filter((l) => l.escopo === 'participante').length,
    };
  }, [logsFiltrados]);

  function formatarData(dataISO: string): string {
    const d = new Date(dataISO);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const escopos = [
    { id: 'todos' as const, label: 'Todos', icon: 'ri-apps-line', color: 'text-gray-600' },
    { id: 'empresa' as const, label: 'Empresa', icon: 'ri-building-line', color: 'text-blue-600' },
    { id: 'campanha' as const, label: 'Campanha', icon: 'ri-briefcase-line', color: 'text-amber-600' },
    { id: 'participante' as const, label: 'Premiado', icon: 'ri-user-star-line', color: 'text-emerald-600' },
  ];

  return (
    <BackofficeLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Auditoria de Configurações</h1>
            <p className="text-sm text-gray-500 mt-1">Histórico completo de todas as alterações de configuração por empresa, campanha e premiado</p>
          </div>
        </div>

        {/* Cards de resumo */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-list-line text-gray-500 text-sm"></i></div>
              </div>
              <p className="text-[10px] font-bold text-gray-500 uppercase">Total de Logs</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-[10px] text-gray-400 mt-1">alterações registradas</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-building-line text-blue-600 text-sm"></i></div>
              </div>
              <p className="text-[10px] font-bold text-gray-500 uppercase">Empresa</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.empresa}</p>
            <p className="text-[10px] text-gray-400 mt-1">configs de empresa</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-briefcase-line text-amber-600 text-sm"></i></div>
              </div>
              <p className="text-[10px] font-bold text-gray-500 uppercase">Campanha</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.campanha}</p>
            <p className="text-[10px] text-gray-400 mt-1">configs de campanha</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-star-line text-emerald-600 text-sm"></i></div>
              </div>
              <p className="text-[10px] font-bold text-gray-500 uppercase">Premiado</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.participante}</p>
            <p className="text-[10px] text-gray-400 mt-1">configs de premiado</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="lg:w-64">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Empresa</label>
              <select
                value={empresaFiltro}
                onChange={(e) => { setEmpresaFiltro(e.target.value); setCampanhaFiltro(''); setPagina(1); }}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
              >
                <option value="">Todas as empresas</option>
                {empresasPlataforma.map((e) => (
                  <option key={e.id} value={e.id}>{e.nome_fantasia}</option>
                ))}
              </select>
            </div>
            <div className="lg:w-64">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Campanha</label>
              <select
                value={campanhaFiltro}
                onChange={(e) => { setCampanhaFiltro(e.target.value); setPagina(1); }}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
              >
                <option value="">Todas as campanhas</option>
                {campanhasBackoffice
                  .filter((c) => !empresaFiltro || c.empresaId === empresaFiltro)
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Buscar</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  placeholder="Usuário, config, participante..."
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Filtro de escopo */}
          <div className="mt-3 flex flex-wrap gap-2">
            {escopos.map((esc) => (
              <button
                key={esc.id}
                onClick={() => { setEscopoFiltro(esc.id); setPagina(1); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  escopoFiltro === esc.id
                    ? `${esc.color.replace('text-', 'bg-').replace('600', '50')} ${esc.color} border border-current`
                    : 'bg-gray-50 text-gray-500 border border-gray-100 hover:bg-gray-100'
                }`}
              >
                <i className={`${esc.icon} text-[11px]`}></i>
                {esc.label}
                <span className="ml-0.5">
                  ({esc.id === 'todos' ? stats.total : esc.id === 'empresa' ? stats.empresa : esc.id === 'campanha' ? stats.campanha : stats.participante})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tabela de logs */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Ação</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Escopo</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Empresa</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Detalhes</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Usuário</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody>
                {logsPaginados.map((log) => {
                  const acaoCfg = getAcaoConfig(log.acao);
                  const escopoColors = {
                    empresa: 'bg-blue-50 text-blue-700',
                    campanha: 'bg-amber-50 text-amber-700',
                    participante: 'bg-emerald-50 text-emerald-700',
                  };
                  return (
                    <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-all">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-xs font-bold text-gray-800">{formatarData(log.data).split(' ')[0]}</p>
                        <p className="text-[10px] text-gray-400">{formatarData(log.data).split(' ')[1]}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${acaoCfg.bg} ${acaoCfg.color}`}>
                          <i className={`${acaoCfg.icon} text-[10px]`}></i>
                          {acaoCfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${escopoColors[log.escopo]}`}>
                          {log.escopo === 'empresa' ? 'Empresa' : log.escopo === 'campanha' ? 'Campanha' : 'Premiado'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-800">{log.empresaNome || '-'}</span>
                          {log.campanhaNome && (
                            <span className="text-[10px] text-gray-400">{log.campanhaNome}</span>
                          )}
                          {log.participanteNome && (
                            <span className="text-[10px] text-gray-400">{log.participanteNome}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-700 max-w-xs truncate">{log.detalhes}</p>
                        {log.valorAnterior && log.valorNovo && (
                          <div className="flex items-center gap-1 mt-1 text-[10px]">
                            <span className="text-gray-400 line-through">{log.valorAnterior}</span>
                            <i className="ri-arrow-right-line text-gray-400 text-[10px]"></i>
                            <span className="text-emerald-600 font-bold">{log.valorNovo}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[9px] font-bold text-gray-600">
                            {log.usuario.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-800">{log.usuario}</p>
                            <p className="text-[10px] text-gray-400">{log.usuarioRole}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setDetalheLog(log)}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-all cursor-pointer"
                          title="Ver detalhes"
                        >
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line text-sm"></i></div>
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {logsPaginados.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                      Nenhum log encontrado para os filtros selecionados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Mostrando <span className="font-bold">{(pagina - 1) * itensPorPagina + 1}</span> a{' '}
              <span className="font-bold">{Math.min(pagina * itensPorPagina, logsFiltrados.length)}</span> de{' '}
              <span className="font-bold">{logsFiltrados.length}</span> registros
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                disabled={pagina <= 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line text-sm"></i></div>
              </button>
              <span className="text-xs text-gray-500">
                Página <span className="font-bold text-gray-800">{pagina}</span> de <span className="font-bold text-gray-800">{totalPaginas}</span>
              </span>
              <button
                onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                disabled={pagina >= totalPaginas}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-sm"></i></div>
              </button>
            </div>
          </div>
        </div>

        {/* Modal de detalhes */}
        {detalheLog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetalheLog(null)}>
            <div className="bg-white rounded-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Detalhes da Alteração</h3>
                <button onClick={() => setDetalheLog(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <div className="w-6 h-6 flex items-center justify-center"><i className="ri-close-line text-xl"></i></div>
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">ID do Log</span>
                  <span className="text-xs font-mono font-bold text-gray-800">{detalheLog.id}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Data e Hora</span>
                  <span className="text-xs font-bold text-gray-800">{formatarData(detalheLog.data)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Usuário</span>
                  <div className="text-right">
                    <p className="text-xs font-bold text-gray-800">{detalheLog.usuario}</p>
                    <p className="text-[10px] text-gray-400">{detalheLog.usuarioRole}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Escopo</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                    detalheLog.escopo === 'empresa' ? 'bg-blue-50 text-blue-700' :
                    detalheLog.escopo === 'campanha' ? 'bg-amber-50 text-amber-700' :
                    'bg-emerald-50 text-emerald-700'
                  }`}>
                    {detalheLog.escopo === 'empresa' ? 'Empresa' : detalheLog.escopo === 'campanha' ? 'Campanha' : 'Premiado'}
                  </span>
                </div>
                {detalheLog.empresaNome && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-500">Empresa</span>
                    <span className="text-xs font-bold text-gray-800">{detalheLog.empresaNome}</span>
                  </div>
                )}
                {detalheLog.campanhaNome && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-500">Campanha</span>
                    <span className="text-xs font-bold text-gray-800">{detalheLog.campanhaNome}</span>
                  </div>
                )}
                {detalheLog.participanteNome && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-500">Premiado</span>
                    <span className="text-xs font-bold text-gray-800">{detalheLog.participanteNome}</span>
                  </div>
                )}
                {detalheLog.chaveConfig && detalheLog.chaveConfig !== 'multipla' && detalheLog.chaveConfig !== 'reset' && detalheLog.chaveConfig !== 'heranca' && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-500">Configuração Alterada</span>
                    <span className="text-xs font-mono font-bold text-gray-800">{detalheLog.chaveConfig}</span>
                  </div>
                )}
                {detalheLog.valorAnterior && detalheLog.valorNovo && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Mudança de Valor</p>
                    <div className="flex items-center justify-between">
                      <div className="text-center flex-1">
                        <p className="text-[10px] text-gray-400">Anterior</p>
                        <p className="text-sm font-bold text-gray-500 line-through">{detalheLog.valorAnterior}</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mx-3">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-emerald-600 text-sm"></i></div>
                      </div>
                      <div className="text-center flex-1">
                        <p className="text-[10px] text-gray-400">Novo</p>
                        <p className="text-sm font-bold text-emerald-600">{detalheLog.valorNovo}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Descrição Completa</p>
                  <p className="text-sm text-gray-800">{detalheLog.detalhes}</p>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">IP</span>
                  <span className="text-xs font-mono font-bold text-gray-800">{detalheLog.ip || '-'}</span>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setDetalheLog(null)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </BackofficeLayout>
  );
}